import { supabase } from '../../lib/supabase';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface ChatPerson {
  id: string;
  username: string;
  role: string | null;
  avatar_url: string | null;
}

export interface ChatMessage {
  id: string;
  conversation_id: string;
  sender_id: string;
  body: string;
  created_at: string;
  deleted_at: string | null;
  edited_at?: string | null;
  attachment_path?: string | null;
  attachment_name?: string | null;
  attachment_type?: string | null;
  attachment_size?: number | null;
  mentions?: string[] | null;
}

const MESSAGE_FIELDS =
  'id, conversation_id, sender_id, body, created_at, deleted_at, edited_at, ' +
  'attachment_path, attachment_name, attachment_type, attachment_size, mentions';

// ─── @mentions ───────────────────────────────────────────────────────────────

/**
 * Resolve "@name" tokens in a draft to participant ids.
 *
 * Longest names are matched first so "@Ana Cruz" wins over "@Ana" when both
 * exist. Ids (not names) are stored on the message, so a later rename can't
 * break "was I mentioned?".
 */
export function resolveMentions(body: string, people: ChatPerson[]): string[] {
  if (!body.includes('@')) return [];
  const lower = body.toLowerCase();
  const byLength = [...people].sort((a, b) => b.username.length - a.username.length);
  const ids = new Set<string>();
  for (const person of byLength) {
    if (lower.includes(`@${person.username.toLowerCase()}`)) ids.add(person.id);
  }
  return Array.from(ids);
}

/** Split a body into plain text and mention spans, for highlighted rendering. */
export function splitMentions(
  body: string,
  people: ChatPerson[],
): Array<{ text: string; mention: boolean }> {
  const names = people
    .map((p) => p.username)
    .filter(Boolean)
    .sort((a, b) => b.length - a.length)
    .map((name) => name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  if (names.length === 0) return [{ text: body, mention: false }];

  const pattern = new RegExp(`@(?:${names.join('|')})`, 'gi');
  const parts: Array<{ text: string; mention: boolean }> = [];
  let cursor = 0;
  let match = pattern.exec(body);
  while (match) {
    if (match.index > cursor) parts.push({ text: body.slice(cursor, match.index), mention: false });
    parts.push({ text: match[0], mention: true });
    cursor = match.index + match[0].length;
    match = pattern.exec(body);
  }
  if (cursor < body.length) parts.push({ text: body.slice(cursor), mention: false });
  return parts;
}

export const MESSAGE_PAGE_SIZE = 40;

export interface ChatConversation {
  id: string;
  is_group: boolean;
  title: string | null;
  created_by: string | null;
  last_message_at: string;
  last_message_preview: string | null;
  last_message_sender: string | null;
  /** Everyone except the signed-in user. */
  others: ChatPerson[];
  unread: number;
  last_read_at: string;
}

const err = (e: any, fallback: string) => new Error(e?.message || fallback);

// ─── Directory ───────────────────────────────────────────────────────────────

/**
 * People you can message. Both apps share the `profiles` table, so agents and
 * office staff appear in one directory regardless of which app they use.
 */
export async function searchPeople(term: string, selfId: string): Promise<ChatPerson[]> {
  let query = supabase
    .from('profiles')
    .select('id, username, role, avatar_url')
    .neq('id', selfId)
    .is('deleted_at', null)
    .order('username')
    .limit(30);

  const search = term.trim();
  if (search.length >= 1) query = query.ilike('username', `%${search}%`);

  const { data, error } = await query;
  if (error) throw err(error, 'Unable to load people.');
  return (data || []) as ChatPerson[];
}

// ─── Conversations ───────────────────────────────────────────────────────────

export async function listConversations(selfId: string): Promise<ChatConversation[]> {
  // My memberships (also carries last_read_at for the unread badge).
  const { data: memberships, error: mErr } = await supabase
    .from('chat_participants')
    .select('conversation_id, last_read_at')
    .eq('profile_id', selfId);
  if (mErr) throw err(mErr, 'Unable to load conversations.');

  const ids = (memberships || []).map((m: any) => m.conversation_id);
  if (ids.length === 0) return [];

  const [{ data: convs, error: cErr }, { data: allParts, error: pErr }] = await Promise.all([
    supabase
      .from('chat_conversations')
      .select('id, is_group, title, created_by, last_message_at, last_message_preview, last_message_sender')
      .in('id', ids)
      .order('last_message_at', { ascending: false }),
    supabase.from('chat_participants').select('conversation_id, profile_id').in('conversation_id', ids),
  ]);
  if (cErr) throw err(cErr, 'Unable to load conversations.');
  if (pErr) throw err(pErr, 'Unable to load conversations.');

  // Resolve the other participants' profiles in one round-trip.
  const otherIds = Array.from(
    new Set((allParts || []).map((p: any) => p.profile_id).filter((id: string) => id !== selfId)),
  );
  const peopleById = new Map<string, ChatPerson>();
  if (otherIds.length > 0) {
    const { data: people } = await supabase
      .from('profiles')
      .select('id, username, role, avatar_url')
      .in('id', otherIds);
    for (const person of people || []) peopleById.set(person.id, person as ChatPerson);
  }

  const readAtByConv = new Map<string, string>(
    (memberships || []).map((m: any) => [m.conversation_id, m.last_read_at]),
  );

  // Unread = messages newer than my last_read_at, not written by me.
  const unreadByConv = new Map<string, number>();
  const { data: recent } = await supabase
    .from('chat_messages')
    .select('conversation_id, sender_id, created_at')
    .in('conversation_id', ids)
    .is('deleted_at', null)
    .order('created_at', { ascending: false })
    .limit(500);
  for (const msg of recent || []) {
    if (msg.sender_id === selfId) continue;
    const readAt = readAtByConv.get(msg.conversation_id) || '1970-01-01';
    if (new Date(msg.created_at) > new Date(readAt)) {
      unreadByConv.set(msg.conversation_id, (unreadByConv.get(msg.conversation_id) || 0) + 1);
    }
  }

  const othersByConv = new Map<string, ChatPerson[]>();
  for (const part of allParts || []) {
    if (part.profile_id === selfId) continue;
    const person = peopleById.get(part.profile_id);
    if (!person) continue;
    othersByConv.set(part.conversation_id, [...(othersByConv.get(part.conversation_id) || []), person]);
  }

  return (convs || []).map((c: any) => ({
    ...c,
    others: othersByConv.get(c.id) || [],
    unread: unreadByConv.get(c.id) || 0,
    last_read_at: readAtByConv.get(c.id) || '1970-01-01',
  })) as ChatConversation[];
}

/** Human label for a conversation (group title, or the other person's name). */
export function conversationTitle(conversation: ChatConversation): string {
  if (conversation.title) return conversation.title;
  if (conversation.others.length === 0) return 'Just you';
  if (conversation.others.length === 1) return conversation.others[0].username;
  return conversation.others.map((p) => p.username).join(', ');
}

export async function startDirect(otherId: string): Promise<string> {
  const { data, error } = await supabase.rpc('chat_start_direct', { other_id: otherId });
  if (error) throw err(error, 'Unable to open that conversation.');
  return data as string;
}

export async function startGroup(title: string, memberIds: string[]): Promise<string> {
  const { data, error } = await supabase.rpc('chat_start_group', {
    group_title: title,
    member_ids: memberIds,
  });
  if (error) throw err(error, 'Unable to create the group.');
  return data as string;
}

/** Display name for a message sender, used by desktop notifications. */
export function senderName(conversation: ChatConversation | undefined, senderId: string): string {
  return conversation?.others.find((p) => p.id === senderId)?.username || 'New message';
}

// ─── Conversation management (thread starter) ────────────────────────────────

export async function renameGroup(conversationId: string, title: string) {
  const { error } = await supabase.rpc('chat_rename_group', {
    conv_id: conversationId,
    new_title: title,
  });
  if (error) throw err(error, 'Unable to rename this group.');
}

export async function addMembers(conversationId: string, memberIds: string[]) {
  if (memberIds.length === 0) return;
  const { error } = await supabase.rpc('chat_add_members', {
    conv_id: conversationId,
    member_ids: memberIds,
  });
  if (error) throw err(error, 'Unable to add people.');
}

/** Removing yourself is "leave"; removing someone else requires being the owner. */
export async function removeMember(conversationId: string, memberId: string) {
  const { error } = await supabase.rpc('chat_remove_member', {
    conv_id: conversationId,
    member_id: memberId,
  });
  if (error) throw err(error, 'Unable to remove that person.');
}

export async function deleteConversation(conversationId: string) {
  const { error } = await supabase.rpc('chat_delete_conversation', { conv_id: conversationId });
  if (error) throw err(error, 'Unable to delete this conversation.');
}

// ─── Messages ────────────────────────────────────────────────────────────────

/**
 * One page of messages, newest-first internally but returned oldest -> newest
 * for display. Pass `before` (an ISO created_at) to page backwards through
 * history for infinite scroll.
 */
export async function listMessages(
  conversationId: string,
  options: { limit?: number; before?: string } = {},
): Promise<ChatMessage[]> {
  const limit = options.limit ?? MESSAGE_PAGE_SIZE;
  let query = supabase
    .from('chat_messages')
    .select(MESSAGE_FIELDS)
    .eq('conversation_id', conversationId)
    .is('deleted_at', null)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (options.before) query = query.lt('created_at', options.before);

  const { data, error } = await query;
  if (error) throw err(error, 'Unable to load messages.');
  return ((data || []) as unknown as ChatMessage[]).reverse();
}

// ─── Attachments ─────────────────────────────────────────────────────────────

export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024; // 10 MB

/** Upload to the private bucket. Path starts with the conversation id so the
 *  storage policies can authorise by participation. */
export async function uploadAttachment(conversationId: string, file: File) {
  if (file.size > MAX_ATTACHMENT_BYTES) {
    throw new Error('That file is larger than 10 MB.');
  }
  const safeName = file.name.replace(/[^\w.\-() ]+/g, '_').slice(-80);
  const path = `${conversationId}/${newMessageId()}-${safeName}`;
  const { error } = await supabase.storage
    .from('chat_attachments')
    .upload(path, file, { contentType: file.type || undefined, upsert: false });
  if (error) throw err(error, 'Upload failed.');
  return { path, name: file.name, type: file.type || 'application/octet-stream', size: file.size };
}

/** Short-lived signed URL — the bucket is private. */
export async function attachmentUrl(path: string): Promise<string | null> {
  const { data, error } = await supabase.storage
    .from('chat_attachments')
    .createSignedUrl(path, 60 * 60);
  if (error) return null;
  return data?.signedUrl ?? null;
}

// ─── Reactions ───────────────────────────────────────────────────────────────

export interface ChatReaction {
  message_id: string;
  profile_id: string;
  emoji: string;
}

/** The emoji offered in the quick picker. */
export const QUICK_REACTIONS = ['👍', '❤️', '😂', '🎉', '😮', '🙏'];

export async function listReactions(messageIds: string[]): Promise<ChatReaction[]> {
  if (messageIds.length === 0) return [];
  const out: ChatReaction[] = [];
  // Chunked so a long thread can't blow the URL length limit.
  for (let i = 0; i < messageIds.length; i += 200) {
    const { data, error } = await supabase
      .from('chat_message_reactions')
      .select('message_id, profile_id, emoji')
      .in('message_id', messageIds.slice(i, i + 200));
    if (error) return out;
    out.push(...((data || []) as ChatReaction[]));
  }
  return out;
}

/** Add or remove my reaction (tapping the same emoji twice clears it). */
export async function toggleReaction(messageId: string, profileId: string, emoji: string, on: boolean) {
  if (on) {
    const { error } = await supabase
      .from('chat_message_reactions')
      .insert({ message_id: messageId, profile_id: profileId, emoji });
    // Duplicate key just means it was already there.
    if (error && !`${error.message}`.includes('duplicate')) throw err(error, 'Reaction failed.');
  } else {
    const { error } = await supabase
      .from('chat_message_reactions')
      .delete()
      .eq('message_id', messageId)
      .eq('profile_id', profileId)
      .eq('emoji', emoji);
    if (error) throw err(error, 'Reaction failed.');
  }
}

// ─── Read receipts ───────────────────────────────────────────────────────────

/** When every other participant has read past this timestamp, it's "Seen". */
export async function otherReadTimes(conversationId: string, selfId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from('chat_participants')
    .select('profile_id, last_read_at')
    .eq('conversation_id', conversationId)
    .neq('profile_id', selfId);
  if (error) return [];
  return (data || []).map((row: any) => row.last_read_at as string);
}

/** Client-side id so the optimistic bubble and the realtime echo are the SAME
 *  row — otherwise the echo appends a second copy of every sent message. */
export function newMessageId(): string {
  const c: any = typeof crypto !== 'undefined' ? crypto : undefined;
  if (c?.randomUUID) return c.randomUUID();
  // Fallback for older browsers.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    const v = ch === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export async function sendMessage(
  conversationId: string,
  senderId: string,
  body: string,
  id: string = newMessageId(),
  attachment?: { path: string; name: string; type: string; size: number } | null,
  mentions: string[] = [],
) {
  const text = body.trim();
  if (!text && !attachment) return;
  const { error } = await supabase.from('chat_messages').insert({
    id,
    conversation_id: conversationId,
    sender_id: senderId,
    // A caption is optional when there's a file, but the column is NOT NULL.
    body: text ? text.slice(0, 4000) : (attachment ? attachment.name : ''),
    attachment_path: attachment?.path ?? null,
    attachment_name: attachment?.name ?? null,
    attachment_type: attachment?.type ?? null,
    attachment_size: attachment?.size ?? null,
    mentions,
  });
  if (error) throw err(error, 'Message not sent.');
}

/** Everyone in a conversation, for the members list and the @ picker. */
export async function listParticipants(conversationId: string): Promise<ChatPerson[]> {
  const { data: rows, error } = await supabase
    .from('chat_participants')
    .select('profile_id')
    .eq('conversation_id', conversationId);
  if (error) throw err(error, 'Unable to load members.');
  const ids = (rows || []).map((r: any) => r.profile_id);
  if (ids.length === 0) return [];
  const { data: people } = await supabase
    .from('profiles')
    .select('id, username, role, avatar_url')
    .in('id', ids)
    .order('username');
  return (people || []) as ChatPerson[];
}

export async function editMessage(id: string, body: string) {
  const text = body.trim();
  if (!text) return;
  const { error } = await supabase
    .from('chat_messages')
    .update({ body: text.slice(0, 4000), edited_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw err(error, 'Unable to edit that message.');
}

export async function deleteMessage(id: string) {
  const { error } = await supabase
    .from('chat_messages')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw err(error, 'Unable to delete that message.');
}

export async function markRead(conversationId: string, selfId: string) {
  await supabase
    .from('chat_participants')
    .update({ last_read_at: new Date().toISOString() })
    .eq('conversation_id', conversationId)
    .eq('profile_id', selfId);
}
